import { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    ChatCircle, PaperPlaneRight, ArrowLeft, User, MagnifyingGlass
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

function TimeAgo({ date }) {
    const diff = (Date.now() - new Date(date).getTime()) / 1000;
    if (diff < 60) return <span>now</span>;
    if (diff < 3600) return <span>{Math.floor(diff / 60)}m</span>;
    if (diff < 86400) return <span>{Math.floor(diff / 3600)}h</span>;
    return <span>{Math.floor(diff / 86400)}d</span>;
}

export default function Chat() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeConvo, setActiveConvo] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMsg, setNewMsg] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newUserId, setNewUserId] = useState('');
    const [groupName, setGroupName] = useState('');
    const [groupMembers, setGroupMembers] = useState('');
    const [showGroupForm, setShowGroupForm] = useState(false);
    const messagesEnd = useRef(null);
    const pollRef = useRef(null);
    const currentUserId = user?.user_id || '';

    const fetchConversations = useCallback(async () => {
        try {
            const res = await fetch(`${API}/chat/conversations`, { credentials: 'include', headers: getH() });
            if (res.ok) setConversations(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchConversations(); }, [fetchConversations]);

    const openConvo = async (convo) => {
        setActiveConvo(convo);
        try {
            const res = await fetch(`${API}/chat/${convo.conversation_id}/messages`, {
                credentials: 'include', headers: getH()
            });
            if (res.ok) setMessages(await res.json());
        } catch {}

        // Start polling
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            const last = messages.length > 0 ? messages[messages.length - 1]?.created_at : null;
            const url = last
                ? `${API}/chat/${convo.conversation_id}/messages?after=${encodeURIComponent(last)}`
                : `${API}/chat/${convo.conversation_id}/messages`;
            try {
                const res = await fetch(url, { credentials: 'include', headers: getH() });
                if (res.ok) {
                    const newMsgs = await res.json();
                    if (newMsgs.length > 0) setMessages(prev => [...prev, ...newMsgs.filter(m => !prev.find(p => p.message_id === m.message_id))]);
                }
            } catch {}
        }, 3000);
    };

    useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);
    useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const sendMessage = async () => {
        if (!newMsg.trim() || !activeConvo) return;
        setSending(true);
        try {
            const res = await fetch(`${API}/chat/${activeConvo.conversation_id}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ text: newMsg.trim() }),
            });
            if (res.ok) {
                const msg = await res.json();
                setMessages(prev => [...prev, msg]);
                setNewMsg('');
                fetchConversations();
            }
        } catch { toast.error('Failed to send'); }
        finally { setSending(false); }
    };

    const startNewConvo = async () => {
        if (!newUserId.trim()) return;
        try {
            const res = await fetch(`${API}/chat/conversations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ user_id: newUserId.trim() }),
            });
            if (res.ok) {
                const convo = await res.json();
                setNewUserId('');
                fetchConversations();
                openConvo(convo);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed');
            }
        } catch { toast.error('Failed'); }
    };

    const createGroup = async () => {
        if (!groupName.trim() || !groupMembers.trim()) { toast.error('Name and members required'); return; }
        const memberIds = groupMembers.split(',').map(s => s.trim()).filter(Boolean);
        if (memberIds.length === 0) { toast.error('Add at least one member'); return; }
        try {
            const res = await fetch(`${API}/chat/groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ name: groupName.trim(), member_ids: memberIds }),
            });
            if (res.ok) {
                const convo = await res.json();
                setGroupName(''); setGroupMembers(''); setShowGroupForm(false);
                fetchConversations();
                openConvo(convo);
                toast.success('Group created!');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed');
            }
        } catch { toast.error('Failed'); }
    };

    // Message thread view
    if (activeConvo) {
        const isGroup = activeConvo.is_group;
        const convoTitle = isGroup ? activeConvo.group_name : (activeConvo.other_user?.name || activeConvo.other_user?.email || 'Rider');
        return (
            <Layout>
                <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col h-[calc(100vh-140px)]">
                    <div className="flex items-center gap-3 mb-4">
                        <Button size="sm" variant="ghost" onClick={() => { setActiveConvo(null); if (pollRef.current) clearInterval(pollRef.current); }}>
                            <ArrowLeft size={18} />
                        </Button>
                        <Avatar className="h-8 w-8 border border-border">
                            {!isGroup && <AvatarImage src={activeConvo.other_user?.picture} />}
                            <AvatarFallback className="text-xs bg-muted">{isGroup ? convoTitle[0] : (activeConvo.other_user?.name || '?')[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                            <p className="font-bold text-sm text-foreground">{convoTitle}</p>
                            {isGroup && <p className="text-[10px] text-muted-foreground">{activeConvo.participants?.length || 0} members</p>}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 mb-4 px-1" data-testid="message-thread">
                        {messages.length === 0 ? (
                            <p className="text-center text-muted-foreground text-sm py-8">No messages yet. Say hi!</p>
                        ) : (
                            messages.map(m => {
                                const isMe = m.sender_id === currentUserId;
                                return (
                                    <motion.div key={m.message_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${
                                            isMe ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'
                                        }`}>
                                            {isGroup && !isMe && <p className="text-[10px] font-bold mb-0.5 text-primary">{m.sender_name}</p>}
                                            <p>{m.text}</p>
                                            <p className={`text-[9px] mt-0.5 ${isMe ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                                                <TimeAgo date={m.created_at} />
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })
                        )}
                        <div ref={messagesEnd} />
                    </div>

                    <div className="flex gap-2">
                        <Input value={newMsg} onChange={e => setNewMsg(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendMessage()}
                            placeholder="Type a message..." className="flex-1 rounded-full"
                            data-testid="chat-input" />
                        <Button onClick={sendMessage} disabled={sending || !newMsg.trim()}
                            className="btn-primary rounded-full" data-testid="send-btn">
                            <PaperPlaneRight size={18} />
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    // Conversations list
    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-electric-blue text-glow-blue">MESSAGES</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="chat-title">
                        Chat
                    </h1>
                </motion.div>

                {/* New conversation */}
                <Card className="glass-card mb-4">
                    <CardContent className="p-3 space-y-2">
                        <div className="flex gap-2">
                            <Input value={newUserId} onChange={e => setNewUserId(e.target.value)}
                                placeholder="Enter rider's user ID to start chat..." className="flex-1 text-sm"
                                data-testid="new-chat-input" />
                            <Button size="sm" onClick={startNewConvo} className="btn-primary" data-testid="new-chat-btn">
                                <ChatCircle size={16} className="mr-1" />Chat
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setShowGroupForm(!showGroupForm)} data-testid="new-group-btn">
                                <User size={16} className="mr-1" />Group
                            </Button>
                        </div>
                        {showGroupForm && (
                            <div className="space-y-2 pt-2 border-t border-border/30">
                                <Input value={groupName} onChange={e => setGroupName(e.target.value)}
                                    placeholder="Group name (e.g. Trail Riders)" className="text-sm"
                                    data-testid="group-name-input" />
                                <Input value={groupMembers} onChange={e => setGroupMembers(e.target.value)}
                                    placeholder="Member IDs (comma separated)" className="text-sm"
                                    data-testid="group-members-input" />
                                <Button size="sm" onClick={createGroup} className="btn-primary w-full" data-testid="create-group-btn">
                                    Create Group
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => (
                        <Card key={i} className="glass-card"><CardContent className="p-4 h-16 animate-pulse bg-muted/20" /></Card>
                    ))}</div>
                ) : conversations.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-8 text-center">
                            <ChatCircle size={48} className="text-muted-foreground mx-auto mb-4" />
                            <p className="font-medium text-foreground">No conversations yet</p>
                            <p className="text-sm text-muted-foreground mt-1">Start chatting with other riders!</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-2">
                        {conversations.map(c => {
                            const isGrp = c.is_group;
                            const title = isGrp ? c.group_name : (c.other_user?.name || c.other_user?.email || 'Rider');
                            return (
                            <Card key={c.conversation_id} className="glass-card cursor-pointer hover:border-primary/20 transition-colors"
                                onClick={() => openConvo(c)} data-testid={`convo-${c.conversation_id}`}>
                                <CardContent className="p-3 flex items-center gap-3">
                                    <Avatar className="h-10 w-10 border border-border">
                                        {!isGrp && <AvatarImage src={c.other_user?.picture} />}
                                        <AvatarFallback className={`text-xs ${isGrp ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>{title[0]}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <p className="font-bold text-sm text-foreground">{title}</p>
                                            {isGrp && <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">{c.participants?.length || 0}</span>}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate">{c.last_message || 'No messages'}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-[10px] text-muted-foreground"><TimeAgo date={c.last_message_at} /></p>
                                        {c.unread > 0 && (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold mt-0.5">
                                                {c.unread}
                                            </span>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );})}
                    </div>
                )}
            </div>
        </Layout>
    );
}
